import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Button } from 'tea-component';
import ColumnAligner from '@intelligence/v3/components/TableComponents/ColumnAligner';
import { getHeaderWidthIntelligence, getHeaderIntelligence, getSimpleSortableHeaderIntelligence } from './utils';

import type { TableColumn } from 'tea-component';
import type { IColumnBaseOption, IColumnHeaderOption } from './utils';

import './actions.scss';

/**
 * 按钮配置
 */
interface IAction {
  // key，通过key区分是点击的哪个按钮
  value: string;
  // 按钮名称
  text: string;
}

// Actions Column构建器自定义配置
interface IActionsColumnBuilderOptionCore<T = any> {
  // 列单元格的className
  className?: string;
  // 占位符
  placeholder?: IPlaceholder;
  // 按钮列表
  actions?: IAction[] | null;
  // 点击按钮的回调
  onClick?: (value: string, cvm: T, e?: any) => void;
}

type IActionsColumnBuilderOption<T> = Partial<TableColumn<T>> &
IColumnBaseOption &
IColumnHeaderOption &
IActionsColumnBuilderOptionCore<T>;

const DEFAULT_ACTIONS: IAction[] = [
  {
    value: 'details',
    text: 'dbi_details',
  },
];

/**
 * Actions Column构建器
 * @param actionsColumnBuilderOption Actions Column构建器配置
 * @param actionsColumnBuilderOption.className 列单元格的className
 * @param actionsColumnBuilderOption.placeholder 占位符
 */
const ActionsColumnBuilder = <T extends IRecord = IRecord>(
  actionsColumnBuilderOption?: IActionsColumnBuilderOption<T>,
): TableColumn<T> => {
  const {
    className,
    placeholder = '-',
    key = 'actions',
    headerTranslatable = true,
    headerTranslateKey = 'dbi_actions',
    header = '',
    align = 'right',
    fixed,
    width = 140,
    widthCN,
    actions = DEFAULT_ACTIONS,
    onClick,
  } = actionsColumnBuilderOption || {};

  const { locale, formatMessage } = useIntl();

  const headerWidth = getHeaderWidthIntelligence(locale, { width, widthCN });
  const headerText = getHeaderIntelligence(locale, { headerTranslatable, headerTranslateKey, header });

  return {
    key,
    align,
    fixed,
    width: headerWidth,
    header: (
      <ColumnAligner align={align} width={headerWidth}>
        {getSimpleSortableHeaderIntelligence({ key, align, header: headerText })}
      </ColumnAligner>
    ),
    render: (cvm) => {
      if (!Array.isArray(actions) || actions.length === 0) return placeholder;
      return (
        <ColumnAligner align={align} width={headerWidth}>
          <div className={classnames('dbi-common-column-actions', className, align)}>
            {actions.map((action) => {
              const actionKey = action?.value;
              const actionText = action?.text || '';
              return (
                <Button
                  className="dbi-common-column-action"
                  key={actionKey}
                  type="link"
                  onClick={(e) => {
                    e?.stopPropagation();
                    onClick?.(actionKey, cvm, e);
                  }}
                >
                  {formatMessage({ id: actionText })}
                </Button>
              );
            })}
          </div>
        </ColumnAligner>
      );
    },
  };
};

export default ActionsColumnBuilder;
